import _map from 'lodash/map'
import _get from 'lodash/get'
import _find from 'lodash/find'
import _findIndex from 'lodash/findIndex'
import React from 'react'
import { Field } from 'formik'
import {
  // validators
  combineValidators,
  minNumberValidator,
  maxNumberValidator,
  requiredValidator,
  // normalizers
  combineNormalizers,
  createFixedFloatNormalizer,
  createUpperCaseNormalizer,
  toNumber,
  trimNormalizer,
} from '../../../../_common/core/validation'
import { SimpleField, SelectField, ReactSelectField, CustomToggleField } from '../../../formik/Fields'
import { OverlayTrigger } from '../../../_library/OverlayTrigger'
import { getPermissions } from '../../../_theme/utils'

const range10 = _map(new Array(10), (item, index) => ({ label: index + 1, value: index + 1 }))
const displayNameReg = new RegExp(/([^()\[\]\s-]+)/g)
const displayNameOptions = ['ga', 'rsvp', 'vip', 'bf', 'nye', 'aaa', 'byob', 'byo', 'rv', 'abc']

const DataRow = ({
  user,
  event,
  values,
  errors,
  isEditing,
  isInventoryMode,
  allowPriceEditing,
  isSlotTicket,
  ticketGroupOptions,
  flagTicketHasTimeSlots,
  updateField,
  tableType,
  configs,
  setFieldValue,
  typeName,
  touched,
  initialValues,
}) => {
  const stockLabel = _get(configs, 'messages.stockLabel')
  const showWaitingListMaxQuantity = _get(configs, 'appearance.showWaitingListMaxQuantity', true)
  const hasAdditionalCurrencies = Boolean(values.additionalCurrencies?.length)
  const permissions = getPermissions(event?.self?.role)
  const isAdminOrOwner = permissions.isAdminOrOwner()

  const initialMaxInventory = _get(initialValues, 'maxInventory') || 0
  const maxTicketsPerOrder = _get(values, 'maxQuantity') ? _get(values, 'maxQuantity') : _get(event, 'maxTicketsPerOrder')

  const initialRealStockValue = _get(initialValues, 'realStock', 0)

  const maxInventoryLimit = initialMaxInventory - (initialRealStockValue || 0)

  const BADGE_OPTIONS = [
    { value: 'Almost gone', label: 'Almost gone' },
    { value: 'High demand', label: 'High demand' },
    { value: 'In demand', label: 'In demand' },
    { value: 'Limited stock', label: 'Limited stock' },
    { value: 'Limited time', label: 'Limited time' },
    { value: 'Running low', label: 'Running low' },
    { value: 'Selling fast', label: 'Selling fast' },
    { value: 'Selling out', label: 'Selling out' },
    { value: '5% left', label: '5% left' },
    { value: '10% left', label: '10% left' },
    { value: '15% left', label: '15% left' },
  ];

  return (
    <>
      <div className="row">
        <div className="col-xs-12 col-sm-6 first-field-ticket-form">
          <Field
            name={isSlotTicket ? 'slotGroupName' : 'displayName'}
            label={isSlotTicket ? 'Ticket Group Name' : 'Name'}
            component={SimpleField}
            validate={requiredValidator()}
            onBlurNormalizer={combineNormalizers(
              trimNormalizer,
              createUpperCaseNormalizer(displayNameReg, displayNameOptions),
            )}
          />
        </div>
        {hasAdditionalCurrencies && (isAdminOrOwner || user?.tfStaff || user?.isSuperAdmin) ? (
          <div className="col-xs-12 col-sm-6 first-field-ticket-form additional-currency-price-block">
            <Field
              name="selectedCurrency"
              label="Currency"
              component={SelectField}
              options={values.additionalCurrencies}
              containerClassName="additional-currency-select-field"
            />
            <Field
              name={`currencies_${values.selectedCurrency}`}
              type="number"
              label="Price"
              disabled={!!isEditing && !allowPriceEditing && !isSlotTicket}
              component={SimpleField}
              onBlurNormalizer={combineNormalizers(toNumber, createFixedFloatNormalizer(2))}
              value={_find(values.currencies, item => item.currency === values.selectedCurrency)?.faceValue}
              onChange={(fieldName, value) => {
                const currencyItemIndex = _findIndex(
                  values.currencies,
                  item => item.currency === values.selectedCurrency,
                )
                const updatedCurrencies = [...values.currencies]

                if (currencyItemIndex !== -1) {
                  updatedCurrencies[currencyItemIndex] = {
                    guestFaceValue: updatedCurrencies?.[currencyItemIndex]?.guestFaceValue,
                    currency: values.selectedCurrency,
                    faceValue: value,
                  }
                } else {
                  updatedCurrencies.push({
                    currency: values.selectedCurrency,
                    faceValue: value,
                  })
                }

                setFieldValue('currencies', updatedCurrencies)
              }}
              customError={errors.currencies}
              showErrorOnlyTouched={Boolean(!touched.currencies)}
              disableSet={true}
              formclassname="additional-currency-price-field-block"
              formGroupClassName="additional-currency-price-field ticket"
            />
          </div>
        ) : (
          <div className="col-xs-12 col-sm-6 first-field-ticket-form">
            <Field // need to dicuss --- values.faceValue || values.price
              name="faceValue"
              type="number"
              label="Price"
              disabled={!!isEditing && !allowPriceEditing && !isSlotTicket}
              component={SimpleField}
              validate={combineValidators(requiredValidator(), minNumberValidator(undefined, 0))}
              onBlurNormalizer={combineNormalizers(toNumber, createFixedFloatNormalizer(2))}
            />
          </div>
        )}
        <div className="col-xs-12 col-sm-6 top-field-ticket-form">
          <Field
            name={isInventoryMode ? 'maxInventory' : 'stock'}
            type="number"
            min={isInventoryMode ? maxInventoryLimit : 0}
            label={isInventoryMode ? stockLabel : 'Stock'}
            component={SimpleField}
            validate={combineValidators(
              requiredValidator(),
              minNumberValidator(undefined, isInventoryMode ? maxInventoryLimit : 0),
            )}
            onBlurNormalizer={toNumber}
          />
        </div>
        <div className="col-xs-12 col-sm-6 top-field-ticket-form">
          <Field
            name="quantityIncrement"
            label="Must be bought in multiples of"
            options={range10}
            validate={combineValidators(
              maxNumberValidator('Please increase the maximum order quantity to enable this value', Number(maxTicketsPerOrder)),
            )}
            component={SelectField}
          />
        </div>
        {tableType && (
          <>
            <div className="col-xs-12 col-sm-6 top-field-ticket-form">
              <Field
                name="minGuests"
                type="number"
                min={0}
                max={10}
                label="Minimum Guests Per Reservation"
                component={SimpleField}
                validate={combineValidators(
                  requiredValidator(),
                  minNumberValidator(undefined, 0),
                  maxNumberValidator(values.maxGuests ? undefined : '', +values.maxGuests),
                )}
                onBlurNormalizer={toNumber}
              />
            </div>
            <div className="col-xs-12 col-sm-6 top-field-ticket-form">
              <Field
                name="maxGuests"
                type="number"
                min={0}
                label="Maximum Guests Per Reservation"
                component={SimpleField}
                validate={combineValidators(requiredValidator(), minNumberValidator(undefined, 0))}
                onBlurNormalizer={toNumber}
              />
            </div>
            {hasAdditionalCurrencies && (isAdminOrOwner || user?.tfStaff || user?.isSuperAdmin) ? (
              <div className="col-xs-12 col-sm-6 top-field-ticket-form additional-currency-price-block">
                <Field
                  name="selectedGuestPriceCurrency"
                  label="Currency"
                  component={SelectField}
                  options={values.additionalCurrencies}
                  containerClassName="additional-currency-select-field"
                />
                <Field
                  name={`guest_price_currencies_${values.selectedGuestPriceCurrency}`}
                  type="number"
                  label="Price Per Additional Guest After Minimum"
                  disabled={!!isEditing && !allowPriceEditing && !isSlotTicket}
                  component={SimpleField}
                  onBlurNormalizer={combineNormalizers(toNumber, createFixedFloatNormalizer(2))}
                  value={
                    _find(values.currencies, item => item.currency === values.selectedGuestPriceCurrency)
                      ?.guestFaceValue
                  }
                  onChange={(fieldName, value) => {
                    const currencyItemIndex = _findIndex(
                      values.currencies,
                      item => item.currency === values.selectedGuestPriceCurrency,
                    )
                    const updatedCurrencies = [...values.currencies]

                    if (currencyItemIndex !== -1) {
                      updatedCurrencies[currencyItemIndex] = {
                        faceValue: updatedCurrencies?.[currencyItemIndex]?.faceValue,
                        currency: values.selectedGuestPriceCurrency,
                        guestFaceValue: value,
                      }
                    } else {
                      updatedCurrencies.push({
                        currency: values.selectedGuestPriceCurrency,
                        guestFaceValue: value,
                      })
                    }

                    setFieldValue('currencies', updatedCurrencies)
                  }}
                  customError={errors.currenciesGuest}
                  showErrorOnlyTouched={Boolean(!touched.currencies)}
                  disableSet={true}
                  formclassname="additional-currency-price-field-block"
                  formGroupClassName="additional-currency-price-field ticket"
                />
              </div>
            ) : (
              <div className="col-xs-12 col-sm-6 top-field-ticket-form">
                <Field
                  name="guestPrice"
                  type="number"
                  min={0}
                  label="Price Per Additional Guest After Minimum"
                  component={SimpleField}
                  validate={combineValidators(requiredValidator(), minNumberValidator(undefined, 0))}
                  onBlurNormalizer={toNumber}
                />
              </div>
            )}
            <div className="col-xs-12 col-sm-6 top-field-ticket-form">
              <Field
                name="depositPercent"
                type="number"
                min={0}
                label={
                  <label>
                    {'Online Deposit Percentage '}
                    <OverlayTrigger
                      className="inline-block"
                      tooltip="This will be the percentage paid to secure the reservation.
                          You will need to collect the remaining amount directly."
                      placement="top"
                      id="parcentageFieldTooltip"
                      trigger={['click']}
                    >
                      <i className="fa fa-info-circle" />
                    </OverlayTrigger>
                  </label>
                }
                component={SimpleField}
                validate={combineValidators(requiredValidator(), minNumberValidator(undefined, 0))}
                onBlurNormalizer={toNumber}
                touchedOnBlur
              />
            </div>
            <div className="col-xs-12 col-sm-6 top-field-ticket-form">
              <Field
                name="maxQuantity"
                type="number"
                min={0}
                label="Maximum ticket quantity per order"
                component={SimpleField}
                validate={minNumberValidator(undefined, 0)}
                onBlurNormalizer={toNumber}
              />
            </div>
          </>
        )}
        <div className="col-xs-12 col-sm-6 top-field-ticket-form">
          <Field
            name="badgeText"
              label="Show badge on ticket type"
              selected={values.badgeText}
              disabled={values.badgeText && !BADGE_OPTIONS.map(opt => opt.value).includes(values.badgeText)}
              options={[
              { value: '', label: 'No badge'},
              ...BADGE_OPTIONS,
              ...(values.badgeText && ![null, ...BADGE_OPTIONS.map(opt => opt.value)].includes(values.badgeText)
                ? [{ value: values.badgeText, label: values.badgeText }]
                  : [])
              ]}
            component={SelectField}
          />
        </div>
        <div className="div-spacing-15" />
        {!tableType && (
          <>
            <div className="col-xs-12 col-sm-6 ticket-toggle" style={{ marginBottom: 20 }}>
              <Field
                name="displayMaxQuantity"
                offText="Set custom maximum quantity per order"
                component={props => <CustomToggleField {...props} direction="right" />}
                onChange={() => {
                  updateField('maxQuantity', '')
                }}
              />
            </div>
            {values.displayMaxQuantity && (
              <div className="col-xs-12 col-sm-6 top-field-ticket-form">
                <Field
                  name="maxQuantity"
                  type="number"
                  min={0}
                  label="Maximum ticket quantity per order"
                  component={SimpleField}
                  validate={minNumberValidator(undefined, 0)}
                  onBlurNormalizer={toNumber}
                />
              </div>
            )}
          </>
        )}
        {flagTicketHasTimeSlots && (
          <div className="col-xs-12">
            <Field
              name="nestedGroup"
              label="Relative Ticket Types"
              multi={true}
              clearable={true}
              options={ticketGroupOptions}
              component={ReactSelectField}
            />
          </div>
        )}
        {flagTicketHasTimeSlots && <div className="div-spacing-15" />}
      </div>
      {!tableType && showWaitingListMaxQuantity && (
        <div className="row">
          <div className={`col-xs-12 col-sm-6 ticket-toggle`} style={{ marginBottom: 20 }}>
            <Field
              name="displayWaitingListMaxQuantity"
              offText="Set custom maximum quantity for waiting list"
              component={props => <CustomToggleField {...props} direction="right" />}
              onChange={() => {
                updateField('waitingListMaxQuantity', '')
              }}
            />
          </div>
          {values.displayWaitingListMaxQuantity && (
            <div className="col-xs-12 col-sm-6 top-field-ticket-form">
              <Field
                name="waitingListMaxQuantity"
                type="number"
                min={0}
                label="Maximum tickets for waiting list"
                component={SimpleField}
                validate={minNumberValidator(undefined, 0)}
                onBlurNormalizer={toNumber}
              />
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default DataRow
